import { Typography } from '@ethings/ui-toolkit';
import { Box } from '@mui/material';
import type { GridSortModel, DataGridProps as MuiDataGridProps, GridColDef as MuiGridColDef, GridRenderCellParams as MuiGridRenderCellParams } from '@mui/x-data-grid';
import { DataGrid as MuiDataGrid } from '@mui/x-data-grid';
import { FC, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { evaluate } from '../../evaluator';

type GridColDef = MuiGridColDef & {
  template?: string;
}

type GridRenderCellParams = MuiGridRenderCellParams & {
  row: unknown;
  colDef?: GridColDef;
};

interface Sort {
  field: string;
  direction: 'asc' | 'desc';
  type: string;
}

interface DataGridProps extends MuiDataGridProps {
  onSortChange?: (sort: Sort) => void;
  sort?: Sort;
}

const renderCellTemplate = (params: GridRenderCellParams): ReactNode => {
  if (params.colDef && params.colDef.template && params.row) {
    return <>{evaluate(params.colDef.template, params.row as object)}</>;
  }
  return null;
};

const NoResultsOverlay: FC = () => {
  const { t } = useTranslation('common');
  return <Box p={2}><Typography variant='body2'>{t('common.hint.list.no-results')}</Typography></Box>;
};

export const DataGrid: FC<DataGridProps> = ({ onSortChange, sort, ...props }) => {
  const [sortModel, setSortModel] = useState<GridSortModel>();

  const columns = props.columns?.map((col) => {
    if ('template' in col) {
      return {
        ...col,
        renderCell: renderCellTemplate,
      };
    }
    return col;
  }) as GridColDef[];

  useEffect(() => {
    if (sort) {
      setSortModel([{ field: sort.field, sort: sort.direction }]);
    }
  }, [sort]);

  const handleSortChange = (sort: GridSortModel): void => {
    if (sort && sort[0]) {
      const field = sort[0].field;
      const direction = sort[0].sort || 'asc';
      columns.map((col) => {
        if (col.field === field) {
          setSortModel(sort);
          onSortChange?.({ field, direction, type: col.type || '' });
          return;
        }
      });
    }
  };

  return <MuiDataGrid {...props} onSortModelChange={handleSortChange} sortModel={sortModel} columns={columns} components={{ NoResultsOverlay, NoRowsOverlay: NoResultsOverlay }} />;
};
